import { ChangeEvent, Dispatch, RefObject, SetStateAction, useCallback, useState } from 'react'
import { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { ALL_COLUMN_NAMES, ColumnValue, DevicesColumn, IntegrationInstance } from 'models'
import {
  generateColDefs,
  generateSingleColDef,
  insertColDefAtCorrectPosition
} from 'ui/pages/cpi-details/sections/devices/utils'

interface Props {
  gridRef: RefObject<AgGridReact>
  initialColumns?: DevicesColumn[]
  columnValues: ColumnValue[]
  setColumnValues: Dispatch<SetStateAction<ColumnValue[]>>
  getLookup: (columnId: string) => Promise<string[]>
  dataSourceOptions: IntegrationInstance[]
  /* Pinned trailing column (the CPI grid's row actions); omitted where a page has none. */
  buildTrailingColDef?: () => ColDef | null
  /* Applied to every generated colDef, so a page can override the op-view filter and renderer. */
  decorateColDef?: (colDef: ColDef) => ColDef
}

export const withTrailingColDef = (colDefs: ColDef[], trailing: ColDef | null): ColDef[] => {
  if (!trailing) return colDefs

  return [...colDefs.filter((col) => 'field' in col && col.field !== trailing.field), trailing]
}

/**
 * Column management shared by the CPI operational view and the Asset Inventory grid: which
 * columns are on, their colDefs, and the lazy lookup fetch a newly enabled column needs before
 * its set filter can offer values.
 */
export const useRecordsColumns = ({
  gridRef,
  initialColumns = [],
  columnValues,
  setColumnValues,
  getLookup,
  dataSourceOptions,
  buildTrailingColDef,
  decorateColDef
}: Props) => {
  const [columns, setColumns] = useState<DevicesColumn[]>(initialColumns)
  const [colDefs, setColDefs] = useState<ColDef[]>([])
  const [isUpdatingColumns, setIsUpdatingColumns] = useState(false)

  const trailingColDef = useCallback(() => buildTrailingColDef?.() ?? null, [buildTrailingColDef])
  const decorate = useCallback((colDef: ColDef) => decorateColDef?.(colDef) ?? colDef, [decorateColDef])

  const applyColDefs = useCallback(
    (next: ColDef[], deferToGrid = false) => {
      const final = withTrailingColDef(next, trailingColDef())
      setColDefs(final)

      if (!gridRef.current?.api) return

      if (deferToGrid) {
        setTimeout(() => gridRef.current?.api.setGridOption('columnDefs', final), 0)
        return
      }

      gridRef.current.api.setGridOption('columnDefs', final)
    },
    [gridRef, trailingColDef]
  )

  const buildAllColDefs = useCallback(
    async (nextColumns: DevicesColumn[], nextColumnValues: ColumnValue[] = []) => {
      const generated = await generateColDefs({
        columns: nextColumns.filter((column) => column.active),
        columnValues: nextColumnValues,
        dataSourceOptions,
        getLookup,
        setColumnValues
      })

      setColumns(nextColumns)

      return withTrailingColDef(generated.map(decorate), trailingColDef())
    },
    [dataSourceOptions, getLookup, setColumnValues, trailingColDef, decorate]
  )

  const addColumn = useCallback(
    async (columnName: string, nextColumns: DevicesColumn[], nextColumnValues: ColumnValue[], deferToGrid: boolean) => {
      const columnToAdd = nextColumns.find((column) => column.name === columnName && column.active)

      if (!columnToAdd || !gridRef.current?.api) return

      const singleColDef = await generateSingleColDef(
        columnToAdd,
        nextColumnValues,
        dataSourceOptions,
        getLookup,
        setColumnValues
      )

      if (!singleColDef) return

      const currentColDefs = gridRef.current.api.getColumnDefs() || []
      applyColDefs(insertColDefAtCorrectPosition(currentColDefs, decorate(singleColDef), nextColumns), deferToGrid)
    },
    [gridRef, dataSourceOptions, getLookup, setColumnValues, applyColDefs, decorate]
  )

  const handleChangeColumns = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!gridRef.current || isUpdatingColumns) return

      const columnName = event.target.name
      const isEnabling = event.target.checked

      setIsUpdatingColumns(true)

      try {
        const nextColumns = columns.map((column) =>
          column.name === columnName ? { ...column, active: isEnabling } : column
        )
        setColumns(nextColumns)

        if (!isEnabling) {
          setColumnValues(columnValues.filter((value) => value.columnName !== columnName))

          if (gridRef.current.api) {
            const currentColDefs = gridRef.current.api.getColumnDefs() || []
            applyColDefs(
              currentColDefs.filter((colDef) => (colDef as ColDef).field !== columnName),
              true
            )
          }

          return
        }

        // A column hidden by a drag is already in colDefs — reveal it instead of rebuilding.
        const currentColDefs = gridRef.current.api.getColumnDefs() || []
        const existing = currentColDefs.find(
          (col) => (col as ColDef).field === columnName || (col as ColDef).colId === columnName
        )

        if (existing) {
          const revealed = currentColDefs.map((col) =>
            (col as ColDef).field === columnName || (col as ColDef).colId === columnName
              ? { ...col, hide: false }
              : col
          )
          setColDefs(revealed as ColDef[])
          gridRef.current.api.setGridOption('columnDefs', revealed)

          return
        }

        const known = columnValues.find((value) => value.columnName === columnName)

        if (known) {
          await addColumn(columnName, nextColumns, columnValues, true)

          return
        }

        try {
          const values = (await getLookup(columnName)) || []
          const newColumnValue = { columnName: columnName as ALL_COLUMN_NAMES, value: values }

          setColumnValues((previous) => [...previous, newColumnValue])
          await addColumn(columnName, nextColumns, [...columnValues, newColumnValue], false)
        } catch (error) {
          console.error(`Failed to fetch values for column ${columnName}:`, error)
          // The column still goes on — its set filter just opens with no values to offer.
          await addColumn(columnName, nextColumns, columnValues, true)
        }
      } finally {
        setIsUpdatingColumns(false)
      }
    },
    [gridRef, isUpdatingColumns, columns, columnValues, setColumnValues, getLookup, addColumn, applyColDefs]
  )

  const restoreDefaultColumns = useCallback(
    async (defaultColumns: DevicesColumn[]) => {
      const activeNames = defaultColumns.filter((column) => column.active).map((column) => column.name)
      const keptValues = columnValues.filter((value) => activeNames.includes(value.columnName as string))

      setColumnValues(keptValues)
      setColDefs(await buildAllColDefs(defaultColumns, keptValues))
    },
    [columnValues, setColumnValues, buildAllColDefs]
  )

  return {
    columns,
    setColumns,
    colDefs,
    setColDefs,
    isUpdatingColumns,
    buildAllColDefs,
    handleChangeColumns,
    restoreDefaultColumns
  }
}
